import { useParams, useSearchParams } from 'react-router';
import {
  ARTIFACT_TYPES,
  ARTIFACT_TYPE_LABELS,
  CLASSES,
  ITEM_QUALITY_NAMES,
  PROFESSIONS,
  type ArtifactType,
  pageTitle,
} from '@thottbot/shared';
import { useBrowse, useClassIcons, useProfessionIcons } from '../api/client';
import { ClassIcon } from '../components/ClassIcon';
import { ArtifactTable } from '../components/ArtifactTable';
import { Pager } from '../components/Pager';
import { NotFound } from './NotFound';
import { useZoneNames } from '../features/artifact/useZoneNames';

function isType(t: string | undefined): t is ArtifactType {
  return !!t && (ARTIFACT_TYPES as readonly string[]).includes(t);
}

export function Browse() {
  const { type } = useParams();
  const [params, setParams] = useSearchParams();
  const zones = useZoneNames();
  if (!isType(type)) return <NotFound />;
  const page = Number(params.get('page') ?? '1') || 1;
  const result = useBrowse(type, params);
  const set = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set(key, value);
    else next.delete(key);
    if (key !== 'page') next.delete('page'); // a new filter starts from page 1
    setParams(next);
  };
  const label = ARTIFACT_TYPE_LABELS[type].plural;
  const classIcons = useClassIcons();
  const professionIcons = useProfessionIcons();
  return (
    <div className="page">
      <title>{pageTitle(label)}</title>
      <h1>{label}</h1>
      {type === 'ability' && (
        <p className="class-strip" data-testid="class-strip">
          {CLASSES.map((c) => (
            <button
              key={c}
              type="button"
              className={`class-chip${params.get('class') === c ? ' active' : ''}`}
              onClick={() => set('class', params.get('class') === c ? '' : c)}
            >
              <ClassIcon cls={c} url={classIcons.data?.[c]} />
              {c}
            </button>
          ))}
        </p>
      )}
      <div className="filters">
        {(type === 'quest' || type === 'npc' || type === 'object') && (
          <label>
            Zone{' '}
            <select value={params.get('zone') ?? ''} onChange={(e) => set('zone', e.target.value)}>
              <option value="">All zones</option>
              {zones.map((z) => (
                <option key={z} value={z}>
                  {z}
                </option>
              ))}
            </select>
          </label>
        )}
        {type === 'ability' && (
          <label>
            Class{' '}
            <select
              value={params.get('class') ?? ''}
              onChange={(e) => set('class', e.target.value)}
            >
              <option value="">All classes</option>
              {CLASSES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </label>
        )}
        {type === 'recipe' && (
          <p className="class-strip" data-testid="profession-strip">
            {PROFESSIONS.map((p) => (
              <button
                key={p}
                type="button"
                className={`class-chip${params.get('profession') === p ? ' active' : ''}`}
                onClick={() => set('profession', params.get('profession') === p ? '' : p)}
              >
                <ClassIcon cls={p} url={professionIcons.data?.[p]} />
                {p}
              </button>
            ))}
          </p>
        )}
        {type === 'recipe' && (
          <label>
            Profession{' '}
            <select
              value={params.get('profession') ?? ''}
              onChange={(e) => set('profession', e.target.value)}
            >
              <option value="">All professions</option>
              {PROFESSIONS.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>
        )}
        {type === 'item' && (
          <label>
            Quality{' '}
            <select
              value={params.get('quality') ?? ''}
              onChange={(e) => set('quality', e.target.value)}
            >
              <option value="">Any quality</option>
              {ITEM_QUALITY_NAMES.map((q, i) => (
                <option key={q} value={String(i)}>
                  {q}
                </option>
              ))}
            </select>
          </label>
        )}
      </div>
      {result.isPending && <p className="muted">Loading...</p>}
      {result.isError && <p className="error">Could not load {label.toLowerCase()}.</p>}
      {result.data && (
        <>
          <ArtifactTable items={result.data.items} />
          <Pager
            page={page}
            pageSize={result.data.pageSize}
            total={result.data.total}
            onPage={(p) => set('page', String(p))}
          />
        </>
      )}
    </div>
  );
}
